<template>
  <div class="preview-backdrop">
    <!-- update-game-state, submit-player-answer, submit-player-vote are demo only events -->
    <DemoView
      ref="gameViewInstance"
      :username="firstName"
      :gameRoomcode="gameRoomcode"
      :gameState="gameState"
      :primaryColor="primaryColor"
      :isHost="true"
      :totalSecondsRemaining="totalSecondsRemaining"
      :timeRemaining="timeRemaining"
      :key="gameViewKey"
      @play-sound-effect="playSoundEffect($event)"
      @update-game-state="updateGameState($event)"
      @submit-player-answer="submitPlayerAnswer($event)"
      @submit-player-vote="submitPlayerVote($event)"
    />
    <DemoVideoView 
      ref="videoViewInstance"
      :playerUsername="firstName"
      :localTracks="localTracks"
      :gameState="gameState"
      :isHost="true"
      :isLocalAudioMuted="isLocalAudioMuted"
      :isLocalVideoMuted="isLocalVideoMuted"
      :primaryColor="primaryColor"
      :secondaryColor="secondaryColor"
      :backgroundColor="backgroundColor"
      @play-sound-effect="playSoundEffect($event)"
    />
    <GameFooter
      ref="gameFooterInstance"
      :gameRoomcode="gameRoomcode"
      :displayTimer="displayTimer"
      :isLocalAudioMuted="isLocalAudioMuted"
      :isLocalVideoMuted="isLocalVideoMuted"
      :isDemo="true"
      :isHost="true"
      :isMusicMutedInitially="isMusicMutedInitially"
      @disable-local-audio-tracks="disableLocalAudioTracks"
      @enable-local-audio-tracks="enableLocalAudioTracks"
      @disable-local-video-tracks="disableLocalVideoTracks"
      @enable-local-video-tracks="enableLocalVideoTracks"
    />
    <DemoTutorialModal 
      v-if="showTutorialModal"
      @close="showTutorialModal = false"
    />
    <RoundStartView
      ref="roundStartViewInstance"
      v-show="displayRoundStartView"
      :roundIndex="'roundIndex' in gameState ? gameState.roundIndex : -1"
    />
    <transition name="fade-slow">
      <SpinnerButton
        v-show="displayPlayAgainButton"
        class="demo-signup-button"
        :isProcessing="playAgainButtonClicked"
        @click="signup()"
      >
        <!-- Sign Up for Free -->
        Sign Up To Play With Your Team
      </SpinnerButton>
    </transition>
  </div>
</template>

<script>
import DemoVideoView from '@/components/demoComponents/DemoVideoView';
import DemoView from '@/components/demoComponents/DemoView';
import GameFooter from '@/components/gameComponents/GameFooter';
import RoundStartView from '@/components/gameComponents/RoundStartView.vue';
import SpinnerButton from '@/components/utilComponents/SpinnerButton.vue';
import DemoGameStates from '@/components/demoComponents/demoUtils/demoGameStates';
import DemoTutorialModal from '@/components/demoComponents/DemoTutorialModal';

export default {
  name: 'DemoScreen',
  components: {
    DemoVideoView,
    DemoTutorialModal,
    DemoView,
    GameFooter,
    RoundStartView,
    SpinnerButton
  },
  props: {
    firstName: String,
    localTracks: Array,
    isMusicMutedInitially: Boolean,
    wasRedirectedFromDemoSetupScreen: Boolean
  },
  data: () => ({
    auth0UserId: '',
    canLoadGameScreen: false,
    gameRoomcode: 'ABCDEFGH',
    socket: {},
    playerInfo: {},
    gameState: {},
    remoteParticipants: [],
    primaryColor: '',
    secondaryColor: '',
    backgroundColor: '',
    displayTimer: false,
    displayRoundStartView: false,
    isLocalAudioMuted: false,
    isLocalVideoMuted: false,
    displayWeavrSpinner: false,
    displayPlayAgainButton: false,
    playAgainButtonClicked: false,
    displayFollowUpGameMessage: false,
    totalSecondsRemaining: 0,
    timeRemaining: '',
    hasStartedLobbySong: false,
    gameViewKey: 0,
    // pingSetIntervalId: null,
    // timerSetIntervalId: null

    // these are demo specific properties
    showTutorialModal: true,
    timedEvents: [],  // list of all the timed events we trigger in this demo
    answers: [],  // list of answers from the user
    demoGameStates: null, // a class which gives us all of the possible game states during this demo
    playerDidVote: true
  }),
  computed: {
    isTimestampAvailable: function () {
      return 'countdownTimestamp' in this.gameState && this.gameState.countdownTimestamp.length > 0;
    },
    haveAllPlayersAnswered: function () {
      return this.$refs.videoViewInstance.localPlayerHasAnswered && 
        this.$refs.videoViewInstance.remoteParticipants.every(participant => participant.hasAnswered === true);
    },
    numRemoteVotes: function () {
      let count = 0;
      this.$refs.videoViewInstance.remoteParticipants.forEach(participant => {
        if (participant.hasVoted) {
          count += 1;
        }
      });
      return count;
    }
  },
  methods: {
    preventPageUnload (event) {
      event.preventDefault();
      event.returnValue = '';
    },
    submitPlayerAnswer(answer) {
      let new_answer = {
        ...answer,
        username: this.demoGameStates.player.name,
        primaryColor: this.demoGameStates.player.primary,
        score: 40,
        numVotes: 2,
        wasSubmitted: true
      }
      this.answers.push(new_answer);
      // console.log('answers', this.answers);
      if ('prompts' in this.gameState && this.answers.length === this.gameState.prompts.length) {
        this.updateGameState('playerHasAnswered');
      }
    },
    answerPhaseFinished() {
      if (this.haveAllPlayersAnswered) {
        this.updateGameState('goToNextStep');
      }
    },
    submitPlayerVote() {
    // submitPlayerVote(vote) {
      // console.log(vote);
      // console.log(this.gamestate);
      this.updateGameState('playerHasVoted');
    },
    currVotingPhaseFinished() {
      if (this.$refs.videoViewInstance.localPlayerHasVoted && this.numRemoteVotes >= 1) {
        this.updateGameState('goToNextStep');
      }
    },
    updateGameState(event) {
      // console.log('updategame state', event);
      const eventHandlerTable = {
        'getInitialState': () => {
          this.gameState = this.demoGameStates.getInitialState();
          this.getGameState();
        },
        'remoteJoin1': () => {
          this.gameState = this.demoGameStates.getRemoteJoin1State();
          this.getGameState();
        },
        'remoteJoin2': () => {
          this.gameState = this.demoGameStates.getRemoteJoin2State();
          this.getGameState();
        },
        'remoteJoin3': () => {
          this.gameState = this.demoGameStates.getRemoteJoin3State();
          this.getGameState();
        },
        'gameStart': () => {
          this.gameState = this.demoGameStates.getGameStartState();
          this.getGameState();
        },
        'playerHasAnswered': () => {
          this.$refs.videoViewInstance.localPlayerHasAnswered = true;
          this.answerPhaseFinished();
        },
        'remote1HasAnswered': () => {
          this.$refs.videoViewInstance.setPlayerHasAnswered(this.demoGameStates.remoteUser1.id);
          this.answerPhaseFinished();
        },
        'remote2HasAnswered': () => {
          this.$refs.videoViewInstance.setPlayerHasAnswered(this.demoGameStates.remoteUser2.id);
          this.answerPhaseFinished();
        },
        'remote3HasAnswered': () => {
          this.$refs.videoViewInstance.setPlayerHasAnswered(this.demoGameStates.remoteUser3.id);
          this.answerPhaseFinished();
        },
        'remote1HasVoted': () => {
          this.$refs.videoViewInstance.setPlayerHasVoted(this.demoGameStates.remoteUser1.id);
        },
        'remote2HasVoted': () => {
          this.$refs.videoViewInstance.setPlayerHasVoted(this.demoGameStates.remoteUser2.id);
        },
        'remote3HasVoted': () => {
          this.$refs.videoViewInstance.setPlayerHasVoted(this.demoGameStates.remoteUser3.id);
        },
        // these next voting events are a little different from the previous voting events since we need to check if both the player
        // and the ai have voted already
        'playerHasVoted': () => {
          this.$refs.videoViewInstance.localPlayerHasVoted = true;
          this.currVotingPhaseFinished();
        },
        'remote1HasVotedAgain': () => {
          this.$refs.videoViewInstance.setPlayerHasVoted(this.demoGameStates.remoteUser1.id);
          this.currVotingPhaseFinished();
        },
        'remote2HasVotedAgain': () => {
          this.$refs.videoViewInstance.setPlayerHasVoted(this.demoGameStates.remoteUser2.id);
          this.currVotingPhaseFinished();
        },
        // time ran out, so we need to substitute some default prompts in so the user can win
        'timeHasRunOut': () => {
          if (this.answers.length < 2) {
            this.answers = this.demoGameStates.getBackupPlayerAnswers(this.answers);
          }
          this.updateGameState('goToNextStep');
        },
        'timeHasRunOut2': () => {
          this.playerDidVote = false
          this.updateGameState('goToNextStep');
        },
        'skipTurn0': () => {
          // skip the first voting phase since the player did not respond - no need to add a delay because we don't need to display
          // a voting result
          this.updateGameState('goToNextStep');
        },
        'skipTurn1': () => {
          // skip the second voting phase since the player did not respond - add a delay so the voting result can be seen
          // the delay is necessary b/c we don't want to progress to the next step until we show the voting result for the 
          // previous stage
          const VOTE_RESULT_DURATION = this.demoGameStates.VOTE_RESULT_DURATION;
          this.timedEvents.push(setTimeout(() => this.updateGameState('goToNextStep'), VOTE_RESULT_DURATION));
        },
        'goToNextStep': () => {
          this.$refs.gameViewInstance.didTimerReset = false;  //lets us know that the timer has not been reset yet
          const NETWORK_DELAY_DURATION = this.demoGameStates.NETWORK_DELAY_DURATION
          this.timedEvents.push(setTimeout(() => this.updateGameState('goToNextVotingStep'), NETWORK_DELAY_DURATION));
        },
        'goToNextVotingStep': () => {
          // console.log('voting turn: ', this.gameState.turnIndex);
          switch(this.gameState.turnIndex) {
            case -1:
              this.gameState = this.demoGameStates.getVotingState0(this.answers[0]);
              this.$refs.gameViewInstance.updateTurnInfo();
              break;
            case 0:
              this.gameState = this.demoGameStates.getVotingState1(this.answers[0], this.answers[1]);
              this.$refs.gameViewInstance.updateTurnInfo();
              break;
            case 1:
              this.gameState = this.demoGameStates.getVotingState2(this.answers[1]);
              this.playerDidVote = true;
              this.$refs.gameViewInstance.updateTurnInfo();
              break
            case 2:
              this.gameState = this.demoGameStates.getVotingState3(this.playerDidVote);
              this.playerDidVote = true;
              this.$refs.gameViewInstance.updateTurnInfo();
              break
            default:
              this.gameState = this.demoGameStates.GoToNextRound(this.playerDidVote);
              this.playerDidVote = true;
              break
          }
          this.getGameState();
        },
      }
      if (event in eventHandlerTable) {
        eventHandlerTable[event]();
      }
    },
    getGameState () {
      this.setPlayerColors();
      this.processGameState();
      // console.log('demoscreen gameState:', this.gameState);
    },
    setPlayerColors () {
      const playerIndex = this.gameState.playerUserIds.findIndex(userId => userId === this.auth0UserId);
      this.primaryColor = this.gameState.playerPrimaryColors[playerIndex];
      this.secondaryColor = this.gameState.playerSecondaryColors[playerIndex];
      this.backgroundColor = this.gameState.playerBackgroundColors[playerIndex];
    },
    async processGameState () {
      // Stop sound effects just in case
      if (this.gameState.roundIndex >= 0) {
        this.stopAllSoundEffects();
      }
      if (this.gameState.turnIndex < 0) {
        // Round hasn't started yet, so clear gameViewInstance and display prompts in playerViewInstance
        // Stop lobby music once game starts
        if (this.gameState.roundIndex === 0) {
          this.$refs.gameFooterInstance.$refs.musicPlayerInstance.stopSong();
        }
        this.$refs.videoViewInstance.resetPlayersHaveVoted();
        this.$refs.gameViewInstance.displayVoteView = false;
        this.$refs.gameViewInstance.displayVoteResultView = false;
        await (new Promise(resolve => {
          setTimeout(resolve, 400);
        }));
        if (this.gameState.roundIndex >= 0) {
          // // Game is running
          this.$refs.gameViewInstance.displayWaitView = false;
          this.$refs.gameFooterInstance.displayGameRoomcode = true;
          if (this.gameState.roundIndex > 0) {
            // If not the first round, display the previous round's last turn results and round results
            this.displayTimer = false;
            this.$refs.gameViewInstance.previousTurnResult = this.gameState.previousTurnResult;
            this.$refs.gameViewInstance.displayVoteResultView = true;
            await (new Promise(resolve => {
              setTimeout(resolve, 10000);
            }));
            this.$refs.gameViewInstance.displayVoteResultView = false;
            await (new Promise(resolve => {
              setTimeout(resolve, 400);
            }));
            if (this.gameState.roundIndex === this.gameState.numRounds) {
              // game has ended, start playing ending song when showing final scoreboard
              this.$refs.gameFooterInstance.$refs.musicPlayerInstance.playSong('ending');
            }
            this.$refs.gameViewInstance.displayRoundResultView = true;
            this.$refs.gameViewInstance.$refs.roundResultViewInstance.scoreboard = this.gameState.scoreboard;
            await (new Promise(resolve => {
              setTimeout(resolve, 8000);
            }));
            if (this.gameState.roundIndex < this.gameState.numRounds) {
              this.$refs.gameViewInstance.$refs.roundResultViewInstance.scoreboard = [];
              await (new Promise(resolve => {
                setTimeout(resolve, 300);
              }));
              this.$refs.gameViewInstance.displayRoundResultView = false;
            } else {
              // game has ended
              this.$refs.gameViewInstance.displayGameEndView = true;
              await (new Promise(resolve => {
                setTimeout(resolve, 5000);
              }));
              this.$refs.gameViewInstance.displayGameEndView = false;
              this.displayPlayAgainButton = true;
            }
          }

          if (this.gameState.roundIndex < this.gameState.numRounds) {
            this.$refs.gameFooterInstance.$refs.musicPlayerInstance.playSong('prompts');
            this.displayRoundStartView = true;
            await (new Promise(resolve => {
              setTimeout(resolve, 4000);
            }));
            this.displayRoundStartView = false;
            this.displayTimer = true;
            this.updateTimeRemaining();  // ensure this.totalSecondsRemaining is not empty for GameView.PromptTimerAnimation
            this.$refs.gameViewInstance.displayQuestionView = true;
            this.$refs.gameFooterInstance.gameState = this.gameState;
          }
        } else {
          this.$refs.gameViewInstance.displayWaitView = true;
          this.$refs.gameFooterInstance.displayGameRoomcode = false;
          if (!this.hasStartedLobbySong) {
            this.hasStartedLobbySong = true;
            this.$refs.gameFooterInstance.$refs.musicPlayerInstance.playSong('lobby');
          }
        }
      } else {
        // Round has started, clear playerViewInstance, display turns in gameViewInstance
        this.$refs.gameViewInstance.displayWaitView = false;
        this.$refs.gameFooterInstance.displayGameRoomcode = true;
        this.$refs.videoViewInstance.resetPlayersHaveAnswered();
        this.$refs.gameViewInstance.displayQuestionView = false;
        if (this.gameState.turnIndex > 0) {
          // If not the first turn, display the previous turn's result
          this.displayTimer = false;
          this.$refs.gameViewInstance.displayVoteStartView = false;
          this.$refs.videoViewInstance.resetPlayersHaveVoted();
          this.$refs.gameViewInstance.displayVoteView = false;
          this.$refs.gameViewInstance.displayVoteResultView = false;
          await (new Promise(resolve => {
            setTimeout(resolve, 400);
          }));
          this.$refs.gameViewInstance.previousTurnResult = this.gameState.previousTurnResult;
          this.$refs.gameViewInstance.displayVoteResultView = true;
          if ('turnInfo' in this.gameState && this.gameState.turnInfo.wereAllAnswersSubmitted) {
            await (new Promise(resolve => {
              setTimeout(resolve, 10000);
            }));
            this.$refs.gameViewInstance.displayVoteResultView = false;
            await (new Promise(resolve => {
              setTimeout(resolve, 400);
            }));
            this.$refs.gameViewInstance.displayVoteView = true;
            this.$refs.gameViewInstance.turnInfo = this.gameState.turnInfo;
            this.$refs.gameFooterInstance.gameState = this.gameState;
            this.displayTimer = true;
          }
        } else {
          this.$refs.gameFooterInstance.$refs.musicPlayerInstance.playSong('voting');
          this.displayTimer = false;
          await (new Promise(resolve => {
            setTimeout(resolve, 600);
          }));
          this.$refs.gameViewInstance.displayVoteStartView = true;
          if ('turnInfo' in this.gameState && this.gameState.turnInfo.wereAllAnswersSubmitted) {
            await (new Promise(resolve => {
              setTimeout(resolve, 2000);
            }));
            this.$refs.gameViewInstance.displayVoteStartView = false;
            await (new Promise(resolve => {
              setTimeout(resolve, 400);
            }));
            this.$refs.gameViewInstance.displayVoteView = true;
            this.$refs.gameViewInstance.turnInfo = this.gameState.turnInfo;
            this.$refs.gameFooterInstance.gameState = this.gameState;
            this.displayTimer = true;
          }
        }
      }
    },
    disableLocalAudioTracks () {
      this.$refs.videoViewInstance.$refs.localVideoComponentInstance.disableAudioTracks();
      this.isLocalAudioMuted = true;
    },
    enableLocalAudioTracks () {
      this.$refs.videoViewInstance.$refs.localVideoComponentInstance.enableAudioTracks();
      this.isLocalAudioMuted = false;
    },
    disableLocalVideoTracks () {
      this.$refs.videoViewInstance.$refs.localVideoComponentInstance.disableVideoTracks();
      this.isLocalVideoMuted = true;
    },
    enableLocalVideoTracks () {
      this.$refs.videoViewInstance.$refs.localVideoComponentInstance.enableVideoTracks();
      this.isLocalVideoMuted = false;
    },
    updateTimeRemaining () {
      if (this.displayTimer && this.isTimestampAvailable && this.gameState.roundIndex < this.gameState.numRounds) {
        this.$refs.gameViewInstance.demoTimerReset = true;
        this.totalSecondsRemaining = (new Date(this.gameState.countdownTimestamp).getTime() - Date.now()) / 1000;
        const timeLimit = this.gameState.turnIndex === -1 ? 90 : 30;
        if (this.totalSecondsRemaining < timeLimit + 1) {
          let minutesRemaining = '0';
          let secondsRemaining = '00';

          if (this.totalSecondsRemaining > 0) {
            minutesRemaining = String(Math.floor(this.totalSecondsRemaining / 60));
            secondsRemaining = String(Math.floor(this.totalSecondsRemaining % 60));

            if (secondsRemaining < 10) {
              secondsRemaining = '0' + secondsRemaining;
            }
          }
          this.timeRemaining = minutesRemaining + ':' + secondsRemaining;
        } else {
          this.timeRemaining = '';
        }
        // Timer sound effects
        const flooredTotalSecondsRemaining = Math.floor(this.totalSecondsRemaining);
        if (timeLimit === 90) {
          if (flooredTotalSecondsRemaining === 30) {
            this.playSoundEffect('timer-30');
          }
        }
        if (flooredTotalSecondsRemaining === 10) {
          this.playSoundEffect('timer-10');
        } else if (flooredTotalSecondsRemaining === 9) {
          this.playSoundEffect('timer-9-to-1');
        } else if (flooredTotalSecondsRemaining === 0) {
          this.playSoundEffect('timer-0');
        }
      }
    },
    playSoundEffect(soundEffectName) {
      this.$refs.gameFooterInstance.$refs.musicPlayerInstance.playSoundEffect(soundEffectName);
    },
    stopAllSoundEffects() {
      this.$refs.gameFooterInstance.$refs.musicPlayerInstance.stopAllSoundEffects();
    },
    signup() {
      window.removeEventListener('beforeunload', this.preventPageUnload);
      this.$auth.loginWithRedirect({ appState: { targetUrl: '/pricing' }, screen_hint: 'signup' });
    }
  },
  created: function () {
    if (!this.wasRedirectedFromDemoSetupScreen) {
      this.$router.push({
        name: 'landing',
      });
    } else {
      this.canLoadGameScreen = true;
      this.displayWeavrSpinner = true;
      this.demoGameStates = new DemoGameStates(this.firstName, 'Jessica', 'Aubrey', 'Ian')
      this.auth0UserId = this.demoGameStates.player.id
    }
  },
  beforeMount: function () {
    if (this.canLoadGameScreen) {
      window.addEventListener('beforeunload', this.preventPageUnload);
    }
  },
  mounted: function() {
    this.$nextTick(function() {
      if (this.canLoadGameScreen) {
        this.updateGameState('getInitialState');
        this.timedEvents.push(setTimeout(() => this.updateGameState('remoteJoin1'), 4000));
        this.timedEvents.push(setTimeout(() => this.updateGameState('remoteJoin2'), 7000));
        this.timedEvents.push(setTimeout(() => this.updateGameState('remoteJoin3'), 9000));
        this.timerSetIntervalId = setInterval(this.updateTimeRemaining, 1000);
      }
    });
  },
  beforeDestroy: function () {
    if (this.canLoadGameScreen) {
      window.removeEventListener('beforeunload', this.preventPageUnload);
      this.localTracks.forEach(track => {
        track.stop();
      });
      if (this.timedEvents.length > 0) {
        this.timedEvents.forEach(timeout => {
          clearTimeout(timeout);
        });
      }
      if (this.timerSetIntervalId !== null) {
        clearInterval(this.timerSetIntervalId);
      }
    }
  }
}
</script>

<style scoped lang="scss">
.demo-signup-button {
  color: $natural-white;
  background-color: $lilac-purple;
  display: flex;
  position: absolute;
  bottom: 4em;
  top: 60%;
  left: 50%;
  font-size: 2em;
  justify-content: center;
  align-items: center;
  transform: translateX(-50%);
  // width: 8em;
  width: 12em;
  // height: 2em;
  height: 3em;
  text-align: center;
  margin: 0 auto;
  border-radius: 0.4em;
  // border: 0.07em solid #00B4A3;
  // background-color: white;
  cursor: pointer;
  // box-shadow: 0 6px 8px -4px #3b3b38;
  border: 0.01em $smoke-gray solid;
  border-radius: 0.4em;
  box-shadow: 0 1px 1px 1px $dim-gray;
  &:hover {
    filter: brightness(1.15);
  }
}
</style>
